import React, { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Avatar,
} from "@mui/material";
import { db, storage } from "../../../firebase/firebase-config";

const STAR_OPTIONS = [
  { value: 5, label: "5 — Excelente" },
  { value: 4, label: "4 — Muy bueno" },
  { value: 3, label: "3 — Bueno" },
  { value: 2, label: "2 — Regular" },
  { value: 1, label: "1 — Malo" },
];

const empty = { name: "", stars: 5, text: "", order: 0 };

async function toWebP(file, size = 200, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      const s = Math.min(img.naturalWidth, img.naturalHeight);
      const sx = (img.naturalWidth - s) / 2;
      const sy = (img.naturalHeight - s) / 2;
      ctx.drawImage(img, sx, sy, s, s, 0, 0, size, size);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Conversión fallida"))),
        "image/webp",
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("No se pudo cargar la imagen")); };
    img.src = url;
  });
}

export default function AddReviewsDialog({ open, onClose, refresh }) {
  const [form, setForm] = useState(empty);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handle = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setError("");
  };

  const handleClose = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview("");
    setForm(empty);
    setError("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) return;
    setLoading(true);
    setError("");
    try {
      const docRef = db.collection("reviews").doc();
      let avatarURL = "";

      if (avatarFile) {
        const blob = await toWebP(avatarFile);
        const ref = storage.ref(`review_avatars/${docRef.id}.webp`);
        await ref.put(blob, { contentType: "image/webp" });
        avatarURL = await ref.getDownloadURL();
      }

      await docRef.set({
        name: form.name,
        text: form.text,
        stars: Number(form.stars) || 5,
        avatarURL,
        order: Number(form.order) || 0,
      });

      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
      setAvatarFile(null);
      setAvatarPreview("");
      setForm(empty);
      if (refresh) refresh();
      onClose();
    } catch (err) {
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundImage: "none" } }}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
          Nueva Reseña
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Nombre"
            value={form.name}
            onChange={handle("name")}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Estrellas"
            select
            value={form.stars}
            onChange={handle("stars")}
            fullWidth
            size="small"
          >
            {STAR_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Reseña"
            value={form.text}
            onChange={handle("text")}
            required
            fullWidth
            multiline
            minRows={3}
            size="small"
          />

          {/* Avatar upload */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Avatar (opcional) — se convierte a WebP 200×200
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                src={avatarPreview || undefined}
                sx={{ width: 56, height: 56, border: "2px solid rgba(255,255,255,0.15)" }}
              >
                {!avatarPreview && (form.name?.[0] ?? "?").toUpperCase()}
              </Avatar>
              <Button component="label" variant="outlined" size="small" disabled={loading}>
                {avatarFile ? "Cambiar imagen" : "Subir imagen"}
                <input type="file" accept="image/*" hidden onChange={handleFile} />
              </Button>
              {avatarFile && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 130 }}>
                  {avatarFile.name}
                </Typography>
              )}
            </Box>
          </Box>

          <TextField
            label="Orden"
            type="number"
            value={form.order}
            onChange={handle("order")}
            fullWidth
            size="small"
          />
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Guardando…" : "Guardar"}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Dialog>
  );
}
